import { Injectable, inject } from '@angular/core';
import { Store, select } from '@ngrx/store';
import {
  ChartedItemScopeResolver,
  TreatmentStep,
  upsertTreatmentsToStep,
} from '@principle-theorem/principle-core';
import {
  type IChartedSurface,
  type IChartedTreatment,
  type IFeeSchedule,
  type IStaffer,
  type ITreatmentConfiguration,
  type ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  serialise,
  snapshot,
  unserialise$,
  type AtLeast,
  type DocumentReference,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TreatmentStepActions } from '../../actions';
import { type IChartState } from '../../reducers/reducers';
import { getSelectedTreatmentPlansTreatmentSteps } from '../../selectors/treatment-plans/core.selectors';
import {
  getSelectedTreatmentStep,
  getTreatmentStepById,
} from '../../selectors/treatment-plans/treatment-steps.selectors';

export interface IMoveTreatmentBetweenPlansParams {
  treatment: IChartedTreatment;
  sourceStep: WithRef<ITreatmentStep>;
  targetStep: WithRef<ITreatmentStep>;
  targetIndex?: number;
}

@Injectable()
export class TreatmentStepsFacade {
  private _store = inject(Store<IChartState>);
  selectedTreatmentStep$: Observable<WithRef<ITreatmentStep> | undefined>;
  selectedPlanSteps$: Observable<WithRef<ITreatmentStep>[] | undefined>;

  constructor() {
    this.selectedTreatmentStep$ = this._store.pipe(
      select(getSelectedTreatmentStep),
      unserialise$()
    );
    this.selectedPlanSteps$ = this._store.pipe(
      select(getSelectedTreatmentPlansTreatmentSteps),
      unserialise$(),
      map(compact)
    );
  }

  selectTreatmentStep(ref: DocumentReference<ITreatmentStep>): void {
    this._store.dispatch(
      TreatmentStepActions.selectTreatmentStep({ id: ref.id })
    );
  }

  getTreatmentStep$(
    id: string
  ): Observable<WithRef<ITreatmentStep> | undefined> {
    return this._store.pipe(select(getTreatmentStepById(id)), unserialise$());
  }

  updateTreatmentStep(
    id: string,
    changes: Partial<WithRef<ITreatmentStep>>
  ): void {
    const update = { id, changes };
    this._store.dispatch(
      TreatmentStepActions.updateTreatmentStep(serialise({ update }))
    );
  }

  async updateTreatmentFromStep(
    treatment: AtLeast<IChartedTreatment, 'uuid'>,
    treatmentStepRef: DocumentReference<ITreatmentStep>
  ): Promise<void> {
    const treatmentStep: WithRef<ITreatmentStep> | undefined = await snapshot(
      this.getTreatmentStep$(treatmentStepRef.id)
    );
    if (!treatmentStep) {
      return;
    }

    treatmentStep.treatments = treatmentStep.treatments.map(
      (currentTreatment) => {
        if (currentTreatment.uuid !== treatment.uuid) {
          return currentTreatment;
        }
        return {
          ...currentTreatment,
          ...treatment,
        };
      }
    );

    this.updateTreatmentStep(treatmentStep.ref.id, treatmentStep);
  }

  async addTreatmentToStep(
    config: WithRef<ITreatmentConfiguration>,
    surfaces: IChartedSurface[],
    treatmentStep: WithRef<ITreatmentStep>,
    feeSchedule: WithRef<IFeeSchedule>,
    attributedTo?: INamedDocument<IStaffer>
  ): Promise<void> {
    const scopeResolver = new ChartedItemScopeResolver();
    const surfaceScopeRefPairs = scopeResolver.reduceChartedSurfacesToScope(
      config,
      surfaces
    );
    const updatedStep = await upsertTreatmentsToStep(
      treatmentStep,
      config,
      surfaceScopeRefPairs,
      scopeResolver,
      feeSchedule,
      attributedTo
    );
    this.updateTreatmentStep(treatmentStep.ref.id, updatedStep);
  }

  async removeTreatmentFromStep(
    treatmentUuid: string,
    treatmentStepRef: DocumentReference<ITreatmentStep>
  ): Promise<void> {
    const treatmentStep: WithRef<ITreatmentStep> | undefined = await snapshot(
      this.getTreatmentStep$(treatmentStepRef.id)
    );
    if (!treatmentStep) {
      return;
    }

    this.updateTreatmentStep(
      treatmentStep.ref.id,
      TreatmentStep.deleteTreatment(treatmentStep, treatmentUuid)
    );
  }

  /**
   * Moves a treatment between plans by adding to the target first, then
   * removing from the source. The rollback in the catch block is best-effort:
   * updateTreatmentStep dispatches NgRx actions whose Firestore persistence
   * happens asynchronously via effects, so the add may already be persisted
   * before the rollback dispatch reaches the effect. A true atomic move would
   * require a Firestore batched write or transaction.
   */
  async moveTreatmentBetweenPlans(
    params: IMoveTreatmentBetweenPlansParams
  ): Promise<void> {
    const { treatment, sourceStep, targetStep, targetIndex } = params;

    const addUpdate =
      targetIndex !== undefined
        ? {
            treatments: [
              ...targetStep.treatments.slice(0, targetIndex),
              treatment,
              ...targetStep.treatments.slice(targetIndex),
            ],
          }
        : TreatmentStep.addTreatment(targetStep, treatment);

    this.updateTreatmentStep(targetStep.ref.id, addUpdate);

    try {
      const currentSourceStep = await snapshot(
        this.getTreatmentStep$(sourceStep.ref.id)
      );
      if (!currentSourceStep) {
        throw new Error(
          `Source step ${sourceStep.ref.id} not found during move`
        );
      }

      this.updateTreatmentStep(
        currentSourceStep.ref.id,
        TreatmentStep.deleteTreatment(currentSourceStep, treatment.uuid)
      );
    } catch (error) {
      this.updateTreatmentStep(
        targetStep.ref.id,
        TreatmentStep.deleteTreatment(targetStep, treatment.uuid)
      );
      throw error;
    }
  }
}
